import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Button, Field } from "../components/rumonai";
import { nodeState, type SupplyNode } from "../hub/modelSupply";
import { platformPost, platformRequest } from "./api";
import { CommandFeedback, usePlatformCommand } from "./shared";

/** What the Hub answers to a new node or a new code. The code is in this answer only; it is never read back. */
interface Issued { node: SupplyNode; code: string; hub_url: string; pair_command: string }

/** How often the list is read again: soon while a code waits to be used, otherwise now and then. */
const WAITING_MS = 3000;
const WATCHING_MS = 15000;

const NODE_ID = /^[a-z][a-z0-9_-]{0,63}$/;
const nodePath = (id: string) => `/api/admin/supply-nodes/${encodeURIComponent(id)}`;

/** The one-time code of a node, with what to do with it on the machine. It is drawn until someone closes it, or
 * until that machine has used it and is connected: then the code is gone and the box says so. */
function IssuedCode({ issued, now, onClose }: { issued: Issued; now: SupplyNode | undefined; onClose: () => void }) {
  const code = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  useLayoutEffect(() => { code.current?.focus(); code.current?.select(); }, []);
  if (now?.online && !now.code_pending) {
    return <div className="platform-invite-result admin-node-issued pa-reveal" role="status">
      <span>「{issued.node.title}」已连上平台。</span>
      <small>连接码已经用掉了。用它提供的服务，在上面「提供的服务」里添加并启用。</small>
      <div className="platform-actions"><Button variant="ghost" onClick={onClose}>收起</Button></div>
    </div>;
  }
  const copy = () => {
    (navigator.clipboard ? navigator.clipboard.writeText(issued.code) : Promise.reject(new Error("no clipboard")))
      .then(() => { setCopied(true); setCopyError(false); }).catch(() => setCopyError(true));
  };
  return <div className="platform-invite-result admin-node-issued pa-reveal" role="status">
    <span>「{issued.node.title}」的连接码</span>
    <small>只显示这一次，10 分钟内有效。关闭后不会再显示。</small>
    <input ref={code} className="text-input" aria-label="一次性连接码" value={issued.code} readOnly
      onFocus={event => event.target.select()} />
    <span>在这台机器的 deploy/gpu_node 目录里：</span>
    <ol className="admin-node-steps">
      <li>把 .env.example 复制成 .env，在里面写上 <code aria-label="Hub 地址">PA_HUB_URL={issued.hub_url}</code></li>
      <li>运行下面的命令，按提示输入连接码：<pre aria-label="配对命令">{issued.pair_command}</pre></li>
      <li>运行 <code>docker compose up -d</code>。已经在运行的连接器会自己换上新的配对。</li>
    </ol>
    <small>配对好、连上以后，这里会显示「已连上平台」。完整步骤见 deploy/gpu_node/README.md。</small>
    {issued.node.paired ? <small>新连接码用掉之前，这台机器现在的连接照常工作；旧的没用过的连接码已作废。</small> : null}
    {copyError ? <small role="alert">浏览器没有允许复制，请手动选择上方连接码。</small> : null}
    <div className="platform-actions">
      <Button variant="secondary" onClick={copy}>{copied ? "已复制连接码" : "复制连接码"}</Button>
      <Button variant="ghost" onClick={onClose}>已记下，收起</Button>
    </div>
  </div>;
}

/** Makes a node. The Hub answers with its first code. */
function NodeForm({ onIssued, onClose }: { onIssued: (issued: Issued) => void; onClose: () => void }) {
  const [id, setId] = useState("");
  const [title, setTitle] = useState("");
  const command = usePlatformCommand();
  const valid = NODE_ID.test(id) && title.trim() !== "";
  const add = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    void command.run(async () => {
      onIssued(await platformRequest<Issued>(nodePath(id), {
        method: "PUT", body: JSON.stringify({ title: title.trim(), reason: "添加供给节点" }),
      }));
    });
  };
  return <form className="platform-admin-form admin-service-form" onSubmit={add}>
    <h3>添加节点</h3>
    <CommandFeedback error={command.error} notice="" />
    <fieldset disabled={command.pending}>
      <Field className="field" label="标识" hint="小写字母、数字、短横线或下划线，以字母开头，最长 64 个字符。添加后不能改。"><input
        className="text-input" name="node_id" required maxLength={64} pattern="[a-z][a-z0-9_\-]{0,63}" autoComplete="off" spellCheck={false}
        value={id} placeholder="例如 home-5090" onChange={event => setId(event.target.value)} /></Field>
      <Field className="field" label="名称"><input
        className="text-input" name="node_title" required maxLength={80} value={title} placeholder="例如 家里的 5090"
        onChange={event => setTitle(event.target.value)} /></Field>
    </fieldset>
    <div className="platform-actions">
      <Button variant="primary" type="submit" disabled={command.pending || !valid}>{command.pending ? "正在添加…" : "添加并获取连接码"}</Button>
      <Button variant="ghost" disabled={command.pending} onClick={onClose}>取消</Button>
    </div>
  </form>;
}

function NodeRow({ node, onIssued, onGone, onChanged }: {
  node: SupplyNode; onIssued: (issued: Issued) => void; onGone: (node: SupplyNode, notice: string) => void; onChanged: () => void;
}) {
  const [action, setAction] = useState<"revoke" | "delete" | null>(null);
  const command = usePlatformCommand();
  const newCode = () => void command.run(async () => {
    onIssued(await platformPost<Issued>(`${nodePath(node.id)}/code`, { reason: "重新获取连接码" }));
    onChanged();
  });
  const apply = () => void command.run(async () => {
    if (action === "revoke") {
      await platformPost(`${nodePath(node.id)}/revoke`, { reason: "停用供给节点" });
      onGone(node, `「${node.title}」已停用，这台机器已断开。需要时重新获取连接码再配对。`);
    } else {
      await platformRequest(nodePath(node.id), { method: "DELETE", body: JSON.stringify({ reason: "删除供给节点" }) });
      onGone(node, `「${node.title}」已删除。`);
    }
    setAction(null);
    onChanged();
  });
  const confirmation = action ? <div className="admin-action-confirm">
    <p>{action === "revoke"
      ? "停用后这台机器马上断开，用它做的服务暂时不能用。之后重新获取连接码，可以再配对。"
      : "删除后这个节点和它的连接码都作废，已经配对的机器也连不上了。还有平台服务在用它时不能删除，先改掉或删掉那些服务。"}</p>
    <div className="platform-actions">
      <Button variant="danger" aria-busy={command.pending || undefined} disabled={command.pending} onClick={apply}>
        {command.pending ? "正在处理…" : action === "revoke" ? "确认停用" : "确认删除"}</Button>
      <Button variant="ghost" disabled={command.pending} onClick={() => setAction(null)}>取消</Button>
    </div>
  </div> : null;
  return <article>
    <div className="platform-instance-summary">
      <div>
        <h3>{node.title}</h3>
        <p>标识：{node.id}</p>
        {node.online && (node.open_requests ?? 0) > 0 ? <p className="platform-meta">正在处理 {node.open_requests} 个请求</p> : null}
      </div>
      <span className={`platform-instance-status${node.online ? " is-online" : ""}`}><i />{nodeState(node)}</span>
    </div>
    <div className="admin-service-actions">
      <div className="platform-actions">
        <Button variant="secondary" disabled={command.pending} onClick={newCode}>重新获取连接码</Button>
        <Button variant="secondary" disabled={command.pending || !node.paired && !node.code_pending}
          onClick={() => setAction("revoke")}>停用</Button>
      </div>
      <CommandFeedback error={command.error} notice="" />
      {action === "revoke" ? confirmation : null}
      <div className="admin-danger">
        {action === "delete" ? confirmation
          : <Button variant="danger" disabled={command.pending} onClick={() => setAction("delete")}>删除</Button>}
      </div>
    </div>
  </article>;
}

/** The machines that make services for the platform: add one, see whether it is connected, cut it off. */
export function SupplyNodes({ nodes, onChanged }: { nodes: SupplyNode[]; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  // Only the latest code is drawn: issuing a code for a node voids its earlier one, and the box says so.
  const [issued, setIssued] = useState<Issued | null>(null);
  const [notice, setNotice] = useState("");
  // The machine connects on its own, so the list is read again while the page is open; a read the page itself
  // asked for (`nodes`) replaces it.
  const [live, setLive] = useState<SupplyNode[] | null>(null);
  useEffect(() => setLive(null), [nodes]);
  const waiting = issued !== null;
  useEffect(() => {
    let stopped = false;
    const read = async () => {
      if (document.hidden) return;
      try {
        const answer = await platformRequest<{ nodes?: SupplyNode[] }>("/api/admin/supply-nodes");
        if (!stopped && Array.isArray(answer.nodes)) setLive(answer.nodes);
      } catch { /* the next read tries again; the page's 刷新 says what is wrong */ }
    };
    const timer = window.setInterval(() => void read(), waiting ? WAITING_MS : WATCHING_MS);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [waiting]);
  const shown = live ?? nodes;
  return <section className="platform-supply-section" aria-labelledby="supply-nodes-heading">
    <div className="platform-explore-tools">
      <h2 id="supply-nodes-heading">供给节点</h2>
      <Button variant="primary" disabled={adding} onClick={() => { setAdding(true); setIssued(null); setNotice(""); }}>
        <Icon name="plus" />添加节点
      </Button>
    </div>
    <p className="platform-meta">替平台提供服务的一台机器。它自己连到 Hub，不需要公网地址。</p>
    {notice ? <CommandFeedback error={null} notice={notice} /> : null}
    {issued ? <IssuedCode key={issued.code} issued={issued} now={shown.find(node => node.id === issued.node.id)}
      onClose={() => setIssued(null)} /> : null}
    {shown.length === 0 && !adding ? <p>还没有供给节点。要用自己的机器提供服务，先添加一个。</p> : null}
    <div className="platform-instance-list">
      {shown.map(node => <NodeRow key={node.id} node={node} onChanged={onChanged}
        onIssued={result => { setNotice(""); setIssued(result); }}
        onGone={(gone, message) => { setNotice(message); setIssued(current => current?.node.id === gone.id ? null : current); }} />)}
    </div>
    {adding ? <NodeForm onClose={() => setAdding(false)}
      onIssued={result => { setIssued(result); setAdding(false); onChanged(); }} /> : null}
  </section>;
}
